import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { SUPPORTED_LANGS } from './i18n.constants';

/** localStorage key of the language the user chose. */
export const LANG_STORAGE_KEY = 'lang';

/**
 * The `localStorage` of `view`, or null when it is unavailable: reading it throws when site data is blocked, and it
 * can also be missing (private mode, tests).
 */
export function getLangStorage(view: Window | null | undefined): Storage | null {
  try {
    return view?.localStorage ?? null;
  } catch {
    return null;
  }
}

/** The language the user chose last time, or null when there is none or the storage cannot be read. */
export function readSavedLang(storage: Storage | null): string | null {
  try {
    return storage?.getItem(LANG_STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
}

/**
 * Switches to `lang` and saves it, so the next start uses it instead of the browser language. Unsupported languages
 * are ignored. If the choice cannot be saved, the language still changes for the current session.
 */
export async function selectLang(
  translate: TranslateService,
  lang: string,
  storage: Storage | null = getLangStorage(window)
): Promise<void> {
  if (!SUPPORTED_LANGS.includes(lang)) return;
  try {
    storage?.setItem(LANG_STORAGE_KEY, lang);
  } catch {
    // Storage full or blocked: keep the choice for this session only.
  }
  await firstValueFrom(translate.use(lang));
}

/**
 * Applies the language chosen in another tab. The `storage` event only fires in the other tabs of the same origin,
 * so the value is applied without saving it again. A removed or unsupported value keeps the current language.
 */
export function followSavedLangChanges(translate: TranslateService, view: Window | null | undefined): void {
  view?.addEventListener('storage', event => {
    if (event.key !== LANG_STORAGE_KEY || event.storageArea !== getLangStorage(view)) return;
    const lang = event.newValue;
    if (lang && SUPPORTED_LANGS.includes(lang) && lang !== translate.currentLang) translate.use(lang);
  });
}
